// frontend/src/components/engineer/components/RecentActivityCard.tsx - PROFESSIONAL UI
import React from 'react';
import { 
  BellIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentPlusIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { RecentActivity } from '../../../services/dashboardStats.service';

interface RecentActivityCardProps {
  activities: RecentActivity[];
  onActivityClick?: (requestId: string) => void;
}

const RecentActivityCard: React.FC<RecentActivityCardProps> = ({ 
  activities, 
  onActivityClick 
}) => {
  
  // Get activity styling - more subtle and professional
  const getActivityConfig = (activity: RecentActivity) => {
    switch (activity.type) {
      case 'created':
        return {
          icon: DocumentPlusIcon,
          label: 'Created',
          dotColor: '#3b82f6',
          bgGradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderColor: '#93c5fd',
          textColor: '#1d4ed8'
        };
      case 'approved':
        return {
          icon: CheckCircleIcon,
          label: 'Approved',
          dotColor: '#10b981',
          bgGradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          borderColor: '#6ee7b7',
          textColor: '#047857'
        };
      case 'rejected':
        return {
          icon: XCircleIcon,
          label: 'Rejected',
          dotColor: '#ef4444',
          bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          borderColor: '#fca5a5',
          textColor: '#dc2626'
        };
      case 'validated':
        if (activity.validationStatus === 'complete') {
          return {
            icon: ShieldCheckIcon,
            label: 'Validated',
            dotColor: '#8b5cf6',
            bgGradient: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
            borderColor: '#c4b5fd',
            textColor: '#6d28d9'
          };
        } else if (activity.validationStatus === 'partial') {
          return {
            icon: ShieldCheckIcon,
            label: 'Partial',
            dotColor: '#f59e0b',
            bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            borderColor: '#fcd34d',
            textColor: '#d97706'
          };
        }
        return {
          icon: ShieldCheckIcon,
          label: 'Validated',
          dotColor: '#6b7280',
          bgGradient: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
          borderColor: '#d1d5db',
          textColor: '#4b5563'
        };
      case 'modified':
        return {
          icon: PencilSquareIcon,
          label: 'Modified',
          dotColor: '#06b6d4',
          bgGradient: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
          borderColor: '#67e8f9',
          textColor: '#0891b2'
        };
      default:
        return {
          icon: ClockIcon,
          label: 'Pending',
          dotColor: '#6b7280',
          bgGradient: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
          borderColor: '#d1d5db',
          textColor: '#4b5563'
        };
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
      border: '1px solid #f0f0f0',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem'
      }}>
        <div style={{
          width: '2rem',
          height: '2rem',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <BellIcon style={{ width: '1.125rem', height: '1.125rem', color: '#d97706' }} />
        </div>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
          Recent Activity
        </h3>
        <div style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.25rem 0.625rem',
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          borderRadius: '1rem',
          border: '1px solid #a7f3d0'
        }}>
          <div style={{
            width: '0.375rem',
            height: '0.375rem',
            background: '#10b981',
            borderRadius: '50%',
            animation: 'pulse 2s infinite'
          }} />
          <span style={{ fontSize: '0.6875rem', fontWeight: '600', color: '#047857' }}>
            LIVE
          </span>
        </div>
      </div>

      {/* Activity List */}
      <div style={{ padding: '0.5rem', maxHeight: '380px', overflowY: 'auto' }}>
        {activities.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {activities.map((activity, index) => {
              const config = getActivityConfig(activity);
              const IconComponent = config.icon;
              
              return (
                <div
                  key={activity.id}
                  onClick={() => onActivityClick?.(activity.requestId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    padding: '0.875rem 1rem',
                    background: '#fafbfc',
                    borderRadius: '0.75rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = config.bgGradient;
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafbfc';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  {/* Status Indicator Line */}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: config.dotColor,
                    borderRadius: '0 4px 4px 0'
                  }} />

                  {/* Icon Container */}
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    background: config.bgGradient,
                    borderRadius: '0.625rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${config.borderColor}`
                  }}>
                    <IconComponent style={{ 
                      width: '1.25rem', 
                      height: '1.25rem', 
                      color: config.textColor 
                    }} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.25rem'
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#1f2937',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {activity.projectName}
                      </p>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem' 
                    }}>
                      <span style={{
                        fontSize: '0.6875rem',
                        fontWeight: '600',
                        color: config.textColor,
                        padding: '0.125rem 0.5rem',
                        background: config.bgGradient,
                        borderRadius: '0.25rem',
                        border: `1px solid ${config.borderColor}`
                      }}>
                        {config.label}
                      </span>
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#6b7280'
                      }}>
                        • {activity.employeeCount} employee{activity.employeeCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Time & Arrow */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '0.25rem'
                  }}>
                    <span style={{
                      fontSize: '0.6875rem',
                      color: '#9ca3af',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      {activity.timeAgo}
                    </span>
                    <ArrowTopRightOnSquareIcon style={{
                      width: '0.875rem',
                      height: '0.875rem',
                      color: '#d1d5db',
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem'
          }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
              borderRadius: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <BellIcon style={{ width: '2rem', height: '2rem', color: '#9ca3af' }} />
            </div>
            <p style={{
              margin: '0 0 0.25rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              color: '#6b7280'
            }}>
              No recent activity
            </p>
            <p style={{
              margin: 0,
              fontSize: '0.8125rem',
              color: '#9ca3af'
            }}>
              Your overtime activities will appear here
            </p>
          </div>
        )}
      </div>

      {/* Footer - View All Link */}
      {activities.length > 0 && (
        <div style={{
          padding: '0.875rem 1.5rem',
          borderTop: '1px solid #f3f4f6',
          textAlign: 'center'
        }}>
          <button
            onClick={() => onActivityClick?.('reports-history')}
            style={{
              background: 'none',
              border: 'none',
              color: '#c29e5d',
              fontSize: '0.8125rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.75rem',
              borderRadius: '0.5rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(194, 158, 93, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            View All Activity
            <ArrowTopRightOnSquareIcon style={{ width: '0.875rem', height: '0.875rem' }} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default RecentActivityCard;